import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Input, Label, Row } from "reactstrap";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import CourseService from "../../../services/KangarooServices/CourseService";
import UniversityService from "../../../services/KangarooServices/UniversityService";
import { toast } from "react-toastify";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/themes/material_green.css";
import * as Yup from "yup";

const AddCourseForm = () => {
  const navigate = useNavigate();
  const { id } = useParams(); 
  const [universities, setUniversities] = useState([]);
  const [courseData, setCourseData] = useState(null);
  console.log(universities)
  

  useEffect(() => {
    const fetchUniversities = async () => {
      try {
        const res = await UniversityService.getList();
        setUniversities(res);
      } catch (err) {
        console.error(err);
      }
    };
    fetchUniversities();
  }, []);

  // Fetch course details if updating
  useEffect(() => {
    if (id) {
      const fetchCourse = async () => {
        try {
          const res = await CourseService.getDetail(id);
          setCourseData(res);
        } catch (err) {
          console.error(err);
        }
      };
      fetchCourse();
    }
  }, [id]);

  // Validation Schema
  const validationSchema = Yup.object({
    name: Yup.string().required("Course name is required"),
    universityId: Yup.string().required("University is required"),
    level: Yup.string().nullable(),
    durationMonths: Yup.number()
      .nullable()
      .min(0, "Duration must be 0 or greater"),
    description: Yup.string().nullable(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      universityId: courseData?.universityId || "",
      name: courseData?.name || "",
      level: courseData?.level || "",
      durationMonths: courseData?.durationMonths || 0,
      tuitionFrom: courseData?.tuitionFrom || 0,
      tuitionTo: courseData?.tuitionTo || 0,
      description: courseData?.description || "",
    },
    validationSchema,
   onSubmit: async (values) => {
  try {
    const payload = {
      ...values,
      tuitionFrom: Number(values.tuitionFrom) || 0,
      tuitionTo: Number(values.tuitionTo) || 0,
    };

    if (id) {
      await CourseService.update(id, payload);
      toast.success("Course updated successfully!");
    } else {
      await CourseService.create(payload);
      toast.success("Course created successfully!");
    }

    navigate("/courses");
  } catch (err) {
    console.error(err.response?.data || err);
    toast.error("Failed to save course");
  }
}
  });

  const breadcrumbItems = [{ title: "Back to List", link: "/courses" }];

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title={id ? "Update Course" : "Add Course"}
          pageTitle="Courses"
          breadcrumbItems={breadcrumbItems}
        />

        <form onSubmit={formik.handleSubmit}>
          <div className="d-flex justify-content-end gap-3 mb-3">
            <button type="submit" className="btn btn-success">
              {id ? "Update" : "Create"}
            </button>
            <button
              type="button"
              className="btn btn-danger"
              onClick={() => navigate("/courses")}
            >
              Cancel
            </button>
          </div>

          <Row>
            {/* LEFT SIDE - Main fields */}
            <Col lg={8}>
              <Card>
                <CardBody>
                  {/* Course Name */}
                  <div className="mb-3">
                    <Label>
                      Course Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="name"
                      type="text"
                      value={formik.values.name}
                      onChange={formik.handleChange}
                      placeholder="Enter course name"
                    />
                    {formik.touched.name && formik.errors.name && (
                      <div className="text-danger mt-1">
                        {formik.errors.name}
                      </div>
                    )}
                  </div>

                  {/* Level */}
                  <div className="mb-3">
                    <Label>Level</Label>
                    <Input
                      name="level"
                      type="text"
                      value={formik.values.level}
                      onChange={formik.handleChange}
                      placeholder="e.g., Beginner, Intermediate"
                    />
                    {formik.touched.level && formik.errors.level && (
                      <div className="text-danger mt-1">
                        {formik.errors.level}
                      </div>
                    )}
                  </div>

                  {/* University */}
                  <div className="mb-3">
                    <Label>
                      University <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="universityId"
                      value={formik.values.universityId}
                      onChange={formik.handleChange}
                    >
                      <option value="" hidden>
                        Select a university
                      </option>
                      {universities.map((uni) => (
                        <option key={uni.id} value={uni.id}>
                          {uni.name}
                        </option>
                      ))}
                    </Input>
                    {formik.touched.universityId && formik.errors.universityId && (
                      <div className="text-danger mt-1">
                        {formik.errors.universityId}
                      </div>
                    )}
                  </div>

                  {/* Description */}
                  <div className="mb-3">
                    <Label>Description</Label>
                    <Input
                      name="description"
                      type="textarea"
                      rows={4}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      placeholder="Enter course description"
                    />
                    {formik.touched.description && formik.errors.description && (
                      <div className="text-danger mt-1">
                        {formik.errors.description}
                      </div>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* RIGHT SIDE - Meta fields */}
            <Col lg={4}>
              <Card>
                <CardBody>
                  {/* Duration */}
                  <div className="mb-3">
                    <Label>Duration (Months)</Label>
                    <Input
                      name="durationMonths"
                      type="number"
                      value={formik.values.durationMonths}
                      onChange={formik.handleChange}
                    />
                    {formik.touched.durationMonths && formik.errors.durationMonths && (
                      <div className="text-danger mt-1">
                        {formik.errors.durationMonths}
                      </div>
                    )}
                  </div>

                  {/* Tuition From */}
                  <div className="mb-3">
                    <Label>Tuition From</Label>
                    <Flatpickr
                      className="form-control"
                      value={formik.values.tuitionFrom ? new Date(formik.values.tuitionFrom) : null}
                      onChange={(date) => {
                        formik.setFieldValue("tuitionFrom", date[0]);
                      }}
                      options={{
                        dateFormat: "Y-m-d", // YYYY-MM-DD
                      }}
                      placeholder="Select start date"
                    />
                  </div>

                  {/* Tuition To */}
                  <div className="mb-3">
                    <Label>Tuition To</Label>
                    <Flatpickr
                      className="form-control"
                      value={formik.values.tuitionTo ? new Date(formik.values.tuitionTo) : null}
                      onChange={(date) => {
                        formik.setFieldValue("tuitionTo", date[0]);
                      }}
                      options={{
                        dateFormat: "Y-m-d",
                      }}
                      placeholder="Select end date"
                    />
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </form>
      </Container>
    </div>
  );
};

export default AddCourseForm;